import { Campaign } from "@common-models/campaign-model";
import { GenericState } from "@common-reducers/DBServiceReducersInterfaces";
import { AppThunkDispatch } from "@common/types/common-root-state-type";
import { getClientType } from "@common/services/detect-context";
// Note: backendApi is imported dynamically to avoid circular dependency

/**
 * Logs newly added campaigns to the frontend client state
 * @param campaigns - Array of campaigns to check
 * @param currentState - Current Redux state for campaigns
 * @param dispatch - Redux dispatch function
 * @param clientId - Optional MQTT client ID
 * @param clientType - Optional client type (frontend, gateway, whatsapp). If not provided, will try to get from MQTTSubscriber
 */
export async function logNewlyAddedCampaigns(
  campaigns: Campaign[],
  currentState: GenericState<Campaign>,
  dispatch: AppThunkDispatch,
  clientId?: string | null,
  clientType?: string | null
): Promise<void> {
  const isDisableCampaignLogging = true;
  if (!campaigns || campaigns.length === 0 || isDisableCampaignLogging) {
    return;
  }

  // Filter campaigns that are newly added and not completed
  const newCampaigns = campaigns.filter((campaign: Campaign) => {
    // 1. Check if campaign is already in state (only log first time client sees it)
    if (!campaign._id || currentState.entities[campaign._id]) {
      return false;
    }

    // 2. Skip logging for completed campaigns
    const completedStatuses = ['completed', 'complete-delivered', 'complete-sent'];
    if (campaign.campaignStatus && completedStatuses.includes(campaign.campaignStatus)) {
      return false;
    }

    return true;
  });

  // Log newly added campaigns
  if (newCampaigns.length > 0) {
    try {
      // Get client type if not provided, fallback to getClientType() function
      // getClientType() returns ClientType enum which is already a lowercase string
      const finalClientType = clientType || getClientType() || 'frontend';

      // Dynamic import to avoid circular dependency
      const { backendApi } = await import(/* webpackMode: "eager" */ "@common-reducers/backend-api/backend-api");

      // Log each newly added campaign
      for (const campaign of newCampaigns) {
        try {
          dispatch(
            backendApi.endpoints.createEntity.initiate({
              service: 'actions',
              entity: {
                cmd: 'create-campaign-log',
                campaignId: campaign._id,
                status: 'campaign-added-to-client',
                source: 'client',
                clientType: finalClientType,
                metadata: {
                  logMessage: `Campaign added to ${finalClientType} client state`,
                  contactCount: campaign.contactCount,
                  clientId: clientId || undefined
                }
              }
            })
          );
        } catch (error) {
          console.error(`Error logging campaign ${campaign._id} addition:`, error);
        }
      }
    } catch (error) {
      console.error('Error logging campaign additions to client:', error);
      // Don't fail the operation if logging fails
    }
  }
}

